import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchRuntimeConfig,
  fetchUsers,
  inviteUser,
  removeUser,
  resetCredentials,
  revokeInvite,
  updateUser,
  type RuntimeConfig,
  type UserRow,
} from '../lib/api.js';

/**
 * User administration.
 *
 * Two things shape this screen:
 *
 *  - There is no self-service sign-up. Access is invite-only: this list IS the
 *    allowlist. Adding someone pre-authorises an address and a role and produces a
 *    one-time enrollment code, which they exchange for a password of their own.
 *    Nothing is emailed, so the code is handed over by whoever invited them.
 *  - Any domain is allowed, on purpose. Subcontractors and partners are not on the
 *    Workspace, and excluding them would either lock them out or force the gate to
 *    be weakened for everyone. External accounts are labelled instead, because they
 *    sit outside your IT control and must be revoked here rather than in Workspace.
 *  - Removing someone must not cost history. An account with nothing behind it is
 *    deleted; one with bids or audited actions is deactivated, and the UI says
 *    which happened rather than pretending they are the same thing.
 */
export function Users({ currentUserId }: { currentUserId: number }) {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  /**
   * Read from the server rather than assumed. This screen makes claims about who
   * can sign in, and those claims are only true if a Workspace domain is actually
   * configured — which, until someone sets ALLOWED_HD_DOMAIN, it is not.
   */
  const [runtime, setRuntime] = useState<RuntimeConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  /**
   * A code that has just been issued. Held in state because the server returns it
   * exactly once — only its hash is stored — so navigating away loses it for good
   * and the only remedy is to issue another.
   */
  const [handoff, setHandoff] = useState<
    { email: string; code: string; kind: 'invite' | 'reset' } | null
  >(null);

  const reload = useCallback(async () => {
    try {
      const [list, config] = await Promise.all([fetchUsers(), fetchRuntimeConfig()]);
      setUsers(list);
      setRuntime(config);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => void reload(), [reload]);

  const handoffRef = useRef<HTMLDivElement | null>(null);

  // `block: 'center'` rather than 'start' so the code is not tucked under the app
  // header. Guarded because jsdom and older Safari do not implement the options.
  useEffect(() => {
    if (!handoff) return;
    handoffRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [handoff]);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await fn());
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      setConfirmingId(null);
    }
  }

  const activeAdmins = (users ?? []).filter((u) => u.role === 'admin' && u.active).length;

  return (
    <>
      {error && <div className="note bad">{error}</div>}
      {notice && <div className="note">{notice}</div>}

      {/* Scrolled to, not just rendered. This panel sits above a list and an invite
          form that together run past a screen, so a code issued from the form at the
          bottom appeared silently off-screen at the top — which reads exactly like
          nothing happened, for the one value in the app that cannot be recovered. */}
      {handoff && (
        <div ref={handoffRef}>
          <CodeHandoff {...handoff} onDismiss={() => setHandoff(null)} />
        </div>
      )}

      {runtime && (
        <div className="note">
          <strong>Access is invite-only.</strong> Only the people listed here can sign in — there is
          no sign-up, and being on a particular email domain grants nothing. Invitations expire
          after {runtime.inviteTtlHours} hours if unused.
          {runtime.internalHdDomain
            ? ` Accounts outside @${runtime.internalHdDomain} are marked external.`
            : ' Set INTERNAL_HD_DOMAIN to have internal and external accounts labelled.'}
        </div>
      )}

      <section className="card">
        <h2>People ({users?.length ?? '…'})</h2>

        {users === null ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>Loading…</div>
        ) : (
          users.map((u) => (
            <div className={`userrow${u.active ? '' : ' inactive'}`} key={u.id}>
              <span className="who">
                <b>
                  {u.name}
                  {u.id === currentUserId && ' (you)'}
                </b>
                <span>
                  {u.email}
                  {u.bidCount > 0 && ` · ${u.bidCount} bid${u.bidCount === 1 ? '' : 's'}`}
                  {u.pending
                    ? u.inviteExpired
                      ? ' · invitation expired'
                      : ` · invited, expires ${u.inviteExpiresAt ? new Date(u.inviteExpiresAt).toLocaleDateString() : 'never'}`
                    : u.lastSeenAt
                      ? ` · last seen ${new Date(u.lastSeenAt).toLocaleDateString()}`
                      : ''}
                </span>
                {!u.active && <span className="badge bad">Deactivated</span>}
                {u.external && (
                  <span className="badge">
                    External{u.hd ? ` · @${u.hd}` : ''}
                  </span>
                )}
                {u.inviteExpired && (
                  <span className="badge bad">Invitation expired — re-invite to restore</span>
                )}
              </span>

              <select
                aria-label={`Role for ${u.email}`}
                value={u.role}
                disabled={busy || !u.active}
                onChange={(e) => {
                  const role = e.target.value as 'estimator' | 'admin';
                  void run(async () => {
                    await updateUser(u.id, { role });
                    return `${u.email} is now ${role === 'admin' ? 'an admin' : 'an estimator'}.`;
                  });
                }}
              >
                <option value="estimator">Estimator</option>
                <option value="admin">Admin</option>
              </select>

              {u.pending ? (
                <>
                  {/* An outstanding code cannot be looked up — only its hash is
                      stored — so the way to get one is to issue a fresh one. This
                      button exists because the alternative was re-typing the person's
                      details into the invite form to trigger the same re-issue. */}
                  <button
                    className="ghost"
                    disabled={busy}
                    title="Issues a new enrollment code and invalidates the previous one"
                    onClick={() =>
                      void run(async () => {
                        const result = await inviteUser(u.email, u.name, u.role);
                        setHandoff({
                          email: u.email,
                          code: result.enrollmentCode,
                          kind: 'invite',
                        });
                        return `New code for ${u.email}. Any previous code no longer works.`;
                      })
                    }
                  >
                    New code
                  </button>
                  <button
                    className="ghost"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await revokeInvite(u.id);
                        return `Invitation for ${u.email} revoked.`;
                      })
                    }
                  >
                    Revoke invite
                  </button>
                </>
              ) : u.active ? (
                <button
                  className="ghost"
                  disabled={busy || u.id === currentUserId}
                  title={u.id === currentUserId ? 'You cannot deactivate yourself' : undefined}
                  onClick={() =>
                    void run(async () => {
                      await updateUser(u.id, { active: false });
                      return `${u.email} can no longer sign in.`;
                    })
                  }
                >
                  Deactivate
                </button>
              ) : (
                <button
                  className="ghost"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await updateUser(u.id, { active: true });
                      return `${u.email} can sign in again.`;
                    })
                  }
                >
                  Reactivate
                </button>
              )}

              {!u.pending && (
                <button
                  className="ghost"
                  // Not on your own row. A reset ends every session it can find,
                  // including the one making the request, so the shown-once code would
                  // appear on a screen that is already signed out — and if the panel
                  // does not survive that, the code is gone and so is your access.
                  // The CLI has no session to lose.
                  disabled={busy || u.id === currentUserId}
                  title={
                    u.id === currentUserId
                      ? 'Resetting your own password would sign you out before you could ' +
                        'use the code. Run: npm run reset-password -- ' + u.email
                      : 'Clears their password and issues a new enrollment code'
                  }
                  onClick={() =>
                    void run(async () => {
                      const result = await resetCredentials(u.id);
                      setHandoff({ email: u.email, code: result.enrollmentCode, kind: 'reset' });
                      return `${u.email} must enroll again with the new code below.`;
                    })
                  }
                >
                  Reset password
                </button>
              )}

              {confirmingId === u.id ? (
                <>
                  <button
                    className="primary"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const outcome = await removeUser(u.id);
                        return outcome.action === 'deleted'
                          ? `${outcome.email} removed.`
                          : outcome.reason;
                      })
                    }
                  >
                    {u.bidCount > 0 || !u.pending ? 'Deactivate' : 'Delete'}
                  </button>
                  <button className="ghost" onClick={() => setConfirmingId(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  className="ghost"
                  disabled={busy || u.id === currentUserId}
                  title={u.id === currentUserId ? 'You cannot remove your own account' : undefined}
                  onClick={() => setConfirmingId(u.id)}
                >
                  Remove
                </button>
              )}
            </div>
          ))
        )}

        {/*(users ?? []).some((u) => u.role === 'admin' && u.external && !u.pending) && (
          <div className="note warn">
            One or more admins are on an <strong>external</strong> address. That is allowed, but
            those accounts are outside your IT control — offboarding them from Workspace will not
            revoke this access, so it has to be done here.
          </div>
        )*/}

        {activeAdmins === 1 && (
          <div className="note warn">
            There is only one active admin. If that account is lost, nobody can change pricing or
            restore access without direct database access.
          </div>
        )}
      </section>

      <InviteForm
        busy={busy}
        internalHdDomain={runtime?.internalHdDomain ?? null}
        inviteTtlHours={runtime?.inviteTtlHours ?? 48}
        onInvite={(email, name, role) =>
          run(async () => {
            const result = await inviteUser(email, name, role);
            setHandoff({ email, code: result.enrollmentCode, kind: 'invite' });
            const when = result.inviteExpiresAt
              ? ` Valid until ${new Date(result.inviteExpiresAt).toLocaleDateString()}.`
              : '';
            return result.reissued
              ? `Invitation for ${email} re-issued with a new code.${when}`
              : `${email} is invited${role === 'admin' ? ' as an admin' : ''}.${when}`;
          })
        }
      />
    </>
  );
}

function InviteForm({
  busy,
  internalHdDomain,
  inviteTtlHours,
  onInvite,
}: {
  busy: boolean;
  internalHdDomain: string | null;
  inviteTtlHours: number;
  onInvite(email: string, name: string, role: 'estimator' | 'admin'): Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'estimator' | 'admin'>('estimator');

  const canSubmit = email.trim() !== '' && name.trim() !== '' && !busy;

  async function submit() {
    if (!canSubmit) return;
    await onInvite(email.trim(), name.trim(), role);
    setEmail('');
    setName('');
    setRole('estimator');
  }

  return (
    <section className="card">
      <h2>Add someone</h2>
      <div className="note">
        Adding someone produces a <strong>one-time enrollment
        code</strong>, shown once on the next screen. Give it to them, and they choose their own
        password. Unused invitations expire after {inviteTtlHours} hours; re-adding the
        same address issues a new code and retires the old one.
        {internalHdDomain && (
          <>
            {' '}
            Anything outside @{internalHdDomain} is marked <em>external</em> in the list.
          </>
        )}
      </div>

      <div className="addform">
        <div className="pair">
          <div className="field" style={{ marginBottom: 0 }}>
            {/* Associated, so tapping the label focuses the field — which matters most
                on a phone — and so a screen reader announces the pairing. */}
            <label htmlFor="invite-name">Name</label>
            <input
              id="invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="invite-email">Email — any domain</label>
            <input
              id="invite-email"
              type="email"
              inputMode="email"
              autoComplete="off"
              placeholder={internalHdDomain ? `name@${internalHdDomain}` : 'name@example.com'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void submit()}
            />
          </div>
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          {/* A group of buttons, not an input, so it carries its own accessible name
              rather than pointing a `for` at something unfocusable. */}
          <label id="invite-role-label">Role</label>
          <div className="seg" role="group" aria-labelledby="invite-role-label">
            {(['estimator', 'admin'] as const).map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={role === r}
                onClick={() => setRole(r)}
              >
                {r === 'admin' ? 'Admin — can change pricing' : 'Estimator — bids only'}
              </button>
            ))}
          </div>
        </div>

        <button className="primary" disabled={!canSubmit} onClick={() => void submit()}>
          Add person
        </button>
      </div>
    </section>
  );
}

/**
 * Shows a freshly issued enrollment code.
 *
 * Prominent and dismissable-only-on-purpose, because this is the one moment the
 * code exists in readable form: the server stores a hash, so once this panel is
 * gone the code cannot be recovered and the only fix is to issue a new one. The
 * copy says exactly that, rather than leaving someone to discover it.
 *
 * The link carries the code in a URL *fragment*. Fragments are never sent to the
 * server, so a code pasted into a chat message does not end up in an access log on
 * the way through.
 */
function CodeHandoff({
  email,
  code,
  kind,
  onDismiss,
}: {
  email: string;
  code: string;
  kind: 'invite' | 'reset';
  onDismiss(): void;
}) {
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const link = `${window.location.origin}/#code=${encodeURIComponent(code)}`;

  async function copy(what: 'code' | 'link', value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
    } catch {
      // Clipboard access is refused in some contexts. The value is on screen and
      // selectable, so this is a missing convenience rather than a failure.
      setCopied(null);
    }
  }

  return (
    <section className="card" style={{ borderColor: '#b98900' }}>
      <h2>{kind === 'reset' ? 'New code for' : 'Enrollment code for'} {email}</h2>

      <div className="note warn">
        <strong>Shown once.</strong> This cannot be looked up again.
        If it is lost, issue a new one. Give it to the user directly.
      </div>

      <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: '1.4rem', letterSpacing: 1 }}>
        {code}
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="ghost" onClick={() => void copy('code', code)}>
          Copy code
        </button>
        <button className="ghost" onClick={() => void copy('link', link)}>
          Copy enrollment link
        </button>
        <button className="primary" onClick={onDismiss}>
          Done
        </button>
        {copied && <span className="muted">{copied === 'code' ? 'Code' : 'Link'} copied.</span>}
      </div>

      <p className="muted">
        {kind === 'reset'
          ? 'Their old password no longer works and any session of theirs has been signed out. '
          : ''}
        They open the app, choose “I have an enrollment code”, and set their own password. The
        link above fills the code in for them.
      </p>
    </section>
  );
}
